import React from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import LaunchIcon from '@mui/icons-material/Launch';
import { Reference } from '../../shared/interfaces';
import { faviconUrlFor } from '../../utils/faviconPreload';

interface InlineCitationProps {
  citationNumber?: number;
  reference?: Reference;
  linkText?: string;
  linkUrl?: string;
}

const CitationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  height: 20px;
  padding: 0 8px;
  margin: 0 2px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'};
  cursor: pointer;
  transition: all 0.15s ease;
  vertical-align: middle;
  line-height: 1;

  &:hover {
    background-color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)'};
  }
`;

const BadgeFavicon = styled.img`
  width: 12px;
  height: 12px;
  border-radius: 2px;
`;

const TooltipContent = styled.div`
  padding: 6px 0;
  max-width: 360px;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const TooltipTitle = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  line-height: 1.4;
`;


const TooltipSnippet = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.5;
`;

const LaunchIconStyled = styled(LaunchIcon)`
  font-size: 0.85rem !important;
  margin-left: 6px;
  opacity: 0.7;
  flex-shrink: 0;
`;

const getDomain = (url: string): string => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace('www.', '');
  } catch {
    return url;
  }
};

const InlineCitation: React.FC<InlineCitationProps> = ({ citationNumber, reference, linkText, linkUrl }) => {
  const url = reference?.link || linkUrl || '';
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!reference && !linkUrl) {
    return <CitationBadge title="Referência não encontrada">{citationNumber ?? linkText}</CitationBadge>;
  }

  const domain = getDomain(url);

  const title = reference?.title || linkText || domain;
  const snippet = reference?.snippet;

  const tooltipContent = (
    <TooltipContent>
      <TooltipTitle>
        {title}
        <LaunchIconStyled />
      </TooltipTitle>
      {snippet && (
        <TooltipSnippet>{snippet}</TooltipSnippet>
      )}
    </TooltipContent>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      enterDelay={200}
      leaveDelay={100}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(33, 33, 33, 0.95)',
            borderRadius: '10px',
            padding: '12px 16px',
            maxWidth: 380,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          },
        },
        arrow: {
          sx: {
            color: 'rgba(33, 33, 33, 0.95)',
          },
        },
      }}
    >
      <CitationBadge onClick={handleClick}>
        <BadgeFavicon
          src={faviconUrlFor(domain)}
          loading="eager"
          decoding="sync"
          alt=""
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        {domain}
      </CitationBadge>
    </Tooltip>
  );
};

export default InlineCitation;
